import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import netSuiteLogo from '../../assets/logos/netsuite-logo.svg';
import openAILogo from '../../assets/logos/openai-logo.svg';
import anthropicLogo from '../../assets/logos/anthropic-logo.svg';
import { 
  faNetworkWired, 
  faPlus, 
  faEdit, 
  faTrash, 
  faCheckCircle,
  faSearch,
  faTh,
  faList,
  faEye
} from '@fortawesome/free-solid-svg-icons';


import MainLayout from '../Layout/MainLayout';
import { connectorAPI } from '../../utils/api';
import toast from 'react-hot-toast';
import AddConnectorModal from './AddConnectorModal';
import NetSuiteConnectorForm from './NetSuiteConnectorForm';
import OpenAIConnectorForm from './OpenAIConnectorForm';
import AnthropicConnectorForm from './AnthropicConnectorForm';
import Microsoft365ConnectorForm from './Microsoft365ConnectorForm';
import ViewConnectorModal from './ViewConnectorModal';

const ConnectorManagement = () => {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showNetSuiteForm, setShowNetSuiteForm] = useState(false);
  const [showOpenAIForm, setShowOpenAIForm] = useState(false);
  const [showAnthropicForm, setShowAnthropicForm] = useState(false);
  const [showMicrosoft365Form, setShowMicrosoft365Form] = useState(false);

  // Edit states
  const [editingConnector, setEditingConnector] = useState(null);
  const [showEditNetSuiteForm, setShowEditNetSuiteForm] = useState(false);
  const [showEditOpenAIForm, setShowEditOpenAIForm] = useState(false);
  const [showEditAnthropicForm, setShowEditAnthropicForm] = useState(false);
  const [showEditMicrosoft365Form, setShowEditMicrosoft365Form] = useState(false);
  
  // View state
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingConnector, setViewingConnector] = useState(null);

  useEffect(() => {
    loadConnectors();
  }, []);

  const loadConnectors = async () => {
    try {
      // Get both available connectors and user credentials
      const [availableResponse, credentialsResponse] = await Promise.all([
        connectorAPI.getConnectors(),
        connectorAPI.getUserConnectors()
      ]);

      if (availableResponse.success && credentialsResponse.success) {
        // Transform user credentials into connector format
        const userConnectors = credentialsResponse.connectors.map((cred, index) => ({
          id: `${cred.connector_name}-${index}`, // Generate unique ID
          name: cred.connector_name,
          // Prefer the real service_type (e.g. "microsoft365") when the backend provides it;
          // fall back to auth_type for connectors that don't carry a service_type.
          service_type: cred.service_type || cred.auth_type,
          status: cred.status || 'active',
          credentials: {}, // Credentials not returned for security
          created_at: cred.created_at,
          last_tested: cred.updated_at || cred.created_at,
          metadata: {}
        }));
        
        setConnectors(userConnectors);
      } else {
        setConnectors([]);
      }
    } catch (error) {
      console.error('Failed to load connectors:', error);
      // Set empty array for now - real connectors will be created via forms
      setConnectors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async (connectorName) => {
    try {
      toast.loading('Testing connection...', { id: 'test-connection' });
      const response = await connectorAPI.testConnection(connectorName);

      // Microsoft 365: open the delegated SSO sign-in so a refresh_token gets stored.
      if (response.auth_url) {
        window.open(response.auth_url, '_blank', 'noopener');
        toast.success('Complete Microsoft sign-in in the new tab. The refresh token will be stored on this connector.', { id: 'test-connection', duration: 8000 });
        return;
      }

      if (response.success) {
        toast.success(response.message || 'Connection test successful!', { id: 'test-connection' });
        loadConnectors(); // Refresh to get updated status
      } else {
        toast.error(response.message || 'Connection test failed', { id: 'test-connection' });
      }
    } catch (error) {
      console.error('Connection test error:', error);
      const errorMessage = error.response?.data?.detail?.message || 
                          error.response?.data?.message || 
                          error.message || 
                          'Connection test failed';
      toast.error(errorMessage, { id: 'test-connection' });
    }
  };

  const handleDeleteConnector = async (connectorName) => {
    if (!window.confirm('Are you sure you want to delete this connector?')) {
      return;
    }

    try {
      const response = await connectorAPI.deleteConnector(connectorName);
      if (response.success) {
        toast.success('Connector deleted successfully');
        loadConnectors();
      }
    } catch (error) {
      toast.error('Failed to delete connector');
      console.error('Connector deletion error:', error);
    }
  };

  const handleAddConnector = () => {
    setShowAddModal(true);
  };

  const handleSelectConnectorType = (type) => {
    setShowAddModal(false);
    
    switch (type) {
      case 'netsuite':
        setShowNetSuiteForm(true);
        break;
      case 'openai':
        setShowOpenAIForm(true);
        break;
      case 'anthropic':
        setShowAnthropicForm(true);
        break;
      case 'microsoft365':
        setShowMicrosoft365Form(true);
        break;
      default:
        console.error('Unknown connector type:', type);
    }
  };

  const handleSaveConnector = async (connectorData) => {
    try {
      const response = await connectorAPI.createConnector(connectorData);
      if (response.success) {
        loadConnectors(); // Refresh the list
        return response;
      } else {
        throw new Error(response.message || 'Failed to create connector');
      }
    } catch (error) {
      console.error('Error creating connector:', error);
      throw error;
    }
  };

  const handleViewConnector = (connector) => {
    setViewingConnector(connector);
    setShowViewModal(true);
  };

  const handleGetConnectorDetails = async (connectorName) => {
    try {
      const response = await connectorAPI.getConnector(connectorName);
      return response;
    } catch (error) {
      console.error('Failed to load connector details:', error);
      throw error;
    }
  };

  const handleEditConnector = async (connector) => {
    try {
      // Load full connector details including credentials for editing
      const fullConnectorData = await connectorAPI.getConnector(connector.name);
      
      if (fullConnectorData.success) {
        // Merge the full data with the connector info
        const enhancedConnector = {
          ...connector,
          credentials: fullConnectorData.credentials,
          metadata: fullConnectorData.metadata
        };
        
        setEditingConnector(enhancedConnector);
        
        switch (connector.service_type) {
          case 'oauth1': // NetSuite uses oauth1
            setShowEditNetSuiteForm(true);
            break;
          case 'api_key':
            if (connector.name.toLowerCase().includes('openai')) {
              setShowEditOpenAIForm(true);
            } else if (connector.name.toLowerCase().includes('anthropic')) {
              setShowEditAnthropicForm(true);
            } else {
              // Default to OpenAI form for generic api_key
              setShowEditOpenAIForm(true);
            }
            break;
          case 'openai': // OpenAI service type
            setShowEditOpenAIForm(true);
            break;
          case 'anthropic': // Anthropic service type
            setShowEditAnthropicForm(true);
            break;
          case 'microsoft365': // Microsoft 365 service type
          case 'oauth2': // Microsoft 365 uses oauth2
            setShowEditMicrosoft365Form(true);
            break;
          case 'custom': {
            // Microsoft 365 / OneDrive connectors are stored with auth_type='custom'
            // (real type lives in metadata.service_type). Detect them by service_type
            // or name (microsoft / 365 / onedrive) and open the Microsoft 365 edit form.
            const n = (connector.name || '').toLowerCase();
            if (connector.service_type === 'microsoft365' ||
                n.includes('microsoft') || n.includes('365') || n.includes('onedrive')) {
              setShowEditMicrosoft365Form(true);
            } else {
              toast.error('Edit not supported for this connector type');
            }
            break;
          }
          default:
            toast.error('Edit not supported for this connector type');
        }
      }
    } catch (error) {
      toast.error('Failed to load connector data for editing');
      console.error('Edit connector error:', error);
    }
  };

  const handleUpdateConnector = async (connectorData) => {
    try {
      if (!editingConnector) {
        throw new Error('No connector selected for editing');
      }
      
      const response = await connectorAPI.updateConnector(editingConnector.name, connectorData);
      if (response.success) {
        loadConnectors(); // Refresh the list
        setEditingConnector(null);
        return response;
      } else {
        throw new Error(response.message || 'Failed to update connector');
      }
    } catch (error) {
      console.error('Error updating connector:', error);
      throw error;
    }
  };


  const filteredConnectors = connectors.filter(connector =>
    connector.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    connector.service_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getConnectorLogo = (serviceType) => {
    switch (serviceType) {
      case 'netsuite':
      case 'oauth1':
        return netSuiteLogo;
      case 'openai':
        return openAILogo;
      case 'anthropic':
        return anthropicLogo;
      case 'microsoft365':
        return null; // Uses gradient icon fallback
      default:
        return null;
    }
  };

  const getConnectorName = (serviceType) => {
    switch (serviceType) {
      case 'netsuite':
      case 'oauth1':
        return 'NetSuite';
      case 'openai':
        return 'OpenAI';
      case 'anthropic':
        return 'Anthropic';
      case 'microsoft365':
        return 'Microsoft 365';
      default:
        return serviceType;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      case 'error':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Never';
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / 60000);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };


  if (loading) {
    return (
      <MainLayout title="Connector Management">
        <div className="p-6 flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="spinner mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading connectors...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Connector Management">
      <div className="p-6 space-y-6">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <button 
            onClick={handleAddConnector}
            className="btn btn-primary"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add Connector
          </button>

          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faSearch} className="text-gray-400 text-sm" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10 w-64"
                placeholder="Search connectors..."
              />
            </div>

            {/* View Toggle */}
            <div className="flex rounded-lg border border-gray-300 dark:border-gray-600">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 rounded-l-lg transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-indigo-500 text-white' 
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <FontAwesomeIcon icon={faTh} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 rounded-r-lg transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-indigo-500 text-white' 
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <FontAwesomeIcon icon={faList} />
              </button>
            </div>
          </div>
        </div>

        {/* Connectors Grid/List */}
        {filteredConnectors.length > 0 ? (
          <div className={`${viewMode === 'grid' ? 'grid grid-cols-1 lg:grid-cols-2 gap-6' : 'space-y-4'}`}>
            {filteredConnectors.map((connector, index) => (
              <motion.div
                key={connector.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card hover:shadow-lg transition-all duration-200"
              >
                {viewMode === 'grid' ? (
                  // Grid View
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-white border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                          {getConnectorLogo(connector.service_type) ? (
                            <img 
                              src={getConnectorLogo(connector.service_type)} 
                              alt={`${getConnectorName(connector.service_type)} Logo`}
                              className="w-10 h-6 object-contain"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-teal-500 rounded-lg flex items-center justify-center text-white text-xl">
                              🔗
                            </div>
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">{connector.name}</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {getConnectorName(connector.service_type)}
                          </p>
                        </div>
                      </div>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(connector.status)}`}>
                        {connector.status}
                      </span>
                    </div>

                    {/* Connection Status */}
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                        Connection Status
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-500">Authentication:</span>
                          <span className="text-xs text-gray-900 dark:text-white font-mono capitalize">
                            {connector.service_type}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-500">Credentials:</span>
                          <span className="text-xs text-green-600 dark:text-green-400">
                            ✓ Configured
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Created:</span>
                        <span className="text-gray-900 dark:text-white">
                          {formatTimeAgo(connector.created_at)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Last tested:</span>
                        <span className="text-gray-900 dark:text-white">
                          {formatTimeAgo(connector.last_tested)}
                        </span>
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleTestConnection(connector.name)}
                        className="btn btn-primary flex-1 text-sm py-2"
                      >
                        <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                        Test
                      </button>
                      <button 
                        onClick={() => handleViewConnector(connector)}
                        className="btn btn-secondary px-3 py-2"
                        title="View Details"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button 
                        onClick={() => handleEditConnector(connector)}
                        className="btn btn-secondary px-3 py-2"
                        title="Edit Connector"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button 
                        onClick={() => handleDeleteConnector(connector.name)}
                        className="btn btn-danger px-3 py-2"
                        title="Delete Connector"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ) : (
                  // List View
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-white border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                          {getConnectorLogo(connector.service_type) ? (
                            <img 
                              src={getConnectorLogo(connector.service_type)} 
                              alt={`${getConnectorName(connector.service_type)} Logo`}
                              className="w-8 h-5 object-contain"
                            />
                          ) : (
                            <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-teal-500 rounded flex items-center justify-center text-white text-sm">
                              🔗
                            </div>
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">{connector.name}</h3>
                          <p className="text-gray-600 dark:text-gray-400 text-sm">
                            {getConnectorName(connector.service_type)} • Last tested {formatTimeAgo(connector.last_tested)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(connector.status)}`}>
                          {connector.status}
                        </span>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleTestConnection(connector.name)}
                            className="btn btn-primary text-sm px-3 py-1"
                            title="Test Connection"
                          >
                            <FontAwesomeIcon icon={faCheckCircle} />
                          </button>
                          <button 
                            onClick={() => handleViewConnector(connector)}
                            className="btn btn-secondary text-sm px-3 py-1"
                            title="View Details"
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                          <button 
                            onClick={() => handleEditConnector(connector)}
                            className="btn btn-secondary text-sm px-3 py-1"
                            title="Edit Connector"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button 
                            onClick={() => handleDeleteConnector(connector.name)}
                            className="btn btn-danger text-sm px-3 py-1"
                            title="Delete Connector"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faNetworkWired} className="text-4xl text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No connectors found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {searchTerm ? 'No connectors match your search criteria.' : 'Get started by adding your first connector.'}
            </p>
            <button 
              onClick={handleAddConnector}
              className="btn btn-primary"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Add Your First Connector
            </button>
          </div>
        )}
        
        {/* Modals */}
        <ViewConnectorModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setViewingConnector(null);
          }}
          connector={viewingConnector}
          onEdit={handleEditConnector}
          onDelete={handleDeleteConnector}
          onTest={handleTestConnection}
          onRefreshData={handleGetConnectorDetails}
        />

        <AddConnectorModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSelectConnectorType={handleSelectConnectorType}
        />
        
        <NetSuiteConnectorForm
          isOpen={showNetSuiteForm}
          onClose={() => setShowNetSuiteForm(false)}
          onSave={handleSaveConnector}
        />
        
        <OpenAIConnectorForm
          isOpen={showOpenAIForm}
          onClose={() => setShowOpenAIForm(false)}
          onSave={handleSaveConnector}
        />
        
        <AnthropicConnectorForm
          isOpen={showAnthropicForm}
          onClose={() => setShowAnthropicForm(false)}
          onSave={handleSaveConnector}
        />

        <Microsoft365ConnectorForm
          isOpen={showMicrosoft365Form}
          onClose={() => setShowMicrosoft365Form(false)}
          onSave={handleSaveConnector}
        />

        {/* Edit Modals */}
        <NetSuiteConnectorForm
          isOpen={showEditNetSuiteForm}
          onClose={() => {
            setShowEditNetSuiteForm(false);
            setEditingConnector(null);
          }}
          onSave={handleUpdateConnector}
          editingConnector={editingConnector}
        />
        
        <OpenAIConnectorForm
          isOpen={showEditOpenAIForm}
          onClose={() => {
            setShowEditOpenAIForm(false);
            setEditingConnector(null);
          }}
          onSave={handleUpdateConnector}
          editingConnector={editingConnector}
        />
        
        <AnthropicConnectorForm
          isOpen={showEditAnthropicForm}
          onClose={() => {
            setShowEditAnthropicForm(false);
            setEditingConnector(null);
          }}
          onSave={handleUpdateConnector}
          editingConnector={editingConnector}
        />

        <Microsoft365ConnectorForm
          isOpen={showEditMicrosoft365Form}
          onClose={() => {
            setShowEditMicrosoft365Form(false);
            setEditingConnector(null);
          }}
          onSave={handleUpdateConnector}
          editingConnector={editingConnector}
        />
      </div>
    </MainLayout>
  );
};

export default ConnectorManagement;